import React, { useEffect, useState } from "react";
import { Info, Plus, Trash2, Pencil, ArrowUp, ArrowDown, X, FileText, Image as ImageIcon } from "lucide-react";
import { api } from "../../lib/api";
import { D, JAKARTA, inputStyle, Card, SectionTitle, Btn, Chip, Field, Toggle, Msg, fmtTime } from "./ui";
import { IconBtn } from "./RadioPanel";

const EMPTY = { title: "", slug: "", content: "", published: true, cover: null, images: [] };

// Unggah gambar (sampul / galeri) untuk halaman Info — tampil di pembaca Info warga
function GambarInfo({ token, form, setForm }) {
  const [prog, setProg] = useState(null);
  async function unggah(e, jenis) {
    const f = e.target.files?.[0]; e.target.value = ""; if (!f) return;
    setProg(0);
    try { const m = await api.uploadMedia(token, f, "image", setProg); setForm(s => jenis === "cover" ? { ...s, cover: `/api/media/${m.id}` } : { ...s, images: [...(s.images || []), `/api/media/${m.id}`] }); }
    catch (er) { alert(er.message); } finally { setProg(null); }
  }
  const kotak = { width: 96, height: 72, borderRadius: 12, background: D.cream || "#f4f1e6", border: `1px dashed ${D.line || "#ddd"}`, display: "grid", placeItems: "center", overflow: "hidden", position: "relative", cursor: "pointer" };
  return (
    <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
      <Field label="Sampul">
        <label data-testid="info-cover-label" style={kotak}>
          {form.cover ? <img src={api.mediaUrl(form.cover)} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <ImageIcon size={18} color={D.gray} />}
          <input data-testid="info-cover-file" type="file" accept="image/*" onChange={e => unggah(e, "cover")} style={{ display: "none" }} />
        </label>
        {form.cover && <button type="button" data-testid="info-cover-hapus" onClick={() => setForm({ ...form, cover: "" })} style={{ marginTop: 4, fontSize: 11, border: 0, background: "none", color: D.gray, cursor: "pointer" }}>hapus sampul</button>}
      </Field>
      <Field label={`Galeri gambar (${(form.images || []).length}/12)`} grow>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {(form.images || []).map((src, i) => <span key={src} data-testid="info-galeri-item" style={kotak}><img src={api.mediaUrl(src)} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /><button type="button" onClick={() => setForm({ ...form, images: form.images.filter((_, j) => j !== i) })} style={{ position: "absolute", top: 4, right: 4, width: 20, height: 20, borderRadius: 99, border: 0, background: "rgba(0,0,0,.6)", color: "#fff", cursor: "pointer", display: "grid", placeItems: "center" }}><X size={11} /></button></span>)}
          {(form.images || []).length < 12 && <label data-testid="info-galeri-label" style={kotak}><Plus size={18} color={D.gray} /><input data-testid="info-galeri-file" type="file" accept="image/*" onChange={e => unggah(e, "galeri")} style={{ display: "none" }} /></label>}
        </div>
        {prog != null && <span style={{ fontSize: 11, color: D.gray }}>Mengunggah… {Math.round(prog * 100)}%</span>}
      </Field>
    </div>
  );
}

// Panel Info: halaman informasi statis (Tentang Jawsel, Cara Bermain, Aturan, Kontak) — urutan bisa diatur
export function InfoPanel({ token }) {
  const [pages, setPages] = useState([]);
  const [form, setForm] = useState(null);
  const [edit, setEdit] = useState(null);
  const [msg, setMsg] = useState("");
  const load = () => api.adminInfo(token).then(d => setPages(d.pages || [])).catch(e => setMsg(e.message));
  useEffect(() => { load(); }, []); // eslint-disable-line

  async function submit(e) {
    e.preventDefault();
    setMsg("");
    try {
      const payload = { ...form, slug: form.slug || undefined };
      if (edit) await api.patchInfo(token, edit, payload); else await api.createInfo(token, payload);
      setForm(null); setEdit(null); setMsg(edit ? "Halaman diperbarui" : "Halaman dibuat"); load();
    } catch (err) { setMsg(err.message); }
  }
  async function move(i, dir) {
    const ids = pages.map(p => p.id); const j = i + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    await api.reorderInfo(token, ids).catch(e => setMsg(e.message)); load();
  }
  async function del(p) {
    if (!window.confirm(`Hapus halaman "${p.title}"?`)) return;
    await api.deleteInfo(token, p.id).catch(e => setMsg(e.message)); load();
  }
  const sel = edit ? pages.find(p => p.id === edit) : null;

  return (
    <div style={{ padding: "22px 20px 30px", fontFamily: JAKARTA }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 18, flexWrap: "wrap" }}>
        <h2 style={{ margin: 0, fontSize: 27, fontWeight: 800, letterSpacing: -0.6, color: D.ink }}>Halaman Info</h2>
        <Chip tone="blue">{pages.filter(p => p.published).length} tayang</Chip>
        <Msg text={msg} testid="info-msg" />
        <div style={{ flex: 1 }} />
        <Btn testid="info-new" onClick={() => { setEdit(null); setForm({ ...EMPTY }); }}><Plus size={15} /> Halaman Baru</Btn>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: form ? "minmax(0, 1fr) minmax(0, 1.2fr)" : "1fr", gap: 18, alignItems: "start" }}>
        <Card testid="info-list-card">
          <SectionTitle title="Daftar halaman" sub="Urutan di sini = urutan menu Info di aplikasi." />
          <div data-testid="info-list" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {pages.map((p, i) => (
              <div key={p.id} data-testid={`info-${p.id}`} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 16, border: `1.5px solid ${edit === p.id ? D.blue : D.line}`, opacity: p.published ? 1 : .6 }}>
                <span style={{ width: 34, height: 34, borderRadius: 11, background: D.blueSoft, display: "grid", placeItems: "center", flex: "none" }}><FileText size={15} color={D.blue} /></span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <b style={{ display: "block", fontSize: 13.5, color: D.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.title}</b>
                  <span style={{ fontSize: 12, color: D.sub }}>/{p.slug} · {fmtTime(p.updated_at)}</span>
                </div>
                <Toggle on={!!p.published} testid={`info-pub-${p.id}`} label="Tayang" onChange={v => api.patchInfo(token, p.id, { published: v }).then(load).catch(e => setMsg(e.message))} />
                <div style={{ display: "flex", gap: 4 }}>
                  <IconBtn testid={`info-up-${p.id}`} onClick={() => move(i, -1)} disabled={i === 0}><ArrowUp size={14} /></IconBtn>
                  <IconBtn testid={`info-down-${p.id}`} onClick={() => move(i, 1)} disabled={i === pages.length - 1}><ArrowDown size={14} /></IconBtn>
                  <IconBtn testid={`info-edit-${p.id}`} onClick={() => { setEdit(p.id); setForm({ title: p.title, slug: p.slug, content: p.content, published: p.published, cover: p.cover || null, images: p.images || [] }); }}><Pencil size={14} /></IconBtn>
                  <IconBtn testid={`info-delete-${p.id}`} danger onClick={() => del(p)}><Trash2 size={14} /></IconBtn>
                </div>
              </div>
            ))}
            {pages.length === 0 && <p data-testid="info-empty" style={{ color: D.sub, fontSize: 13, margin: 0 }}>Belum ada halaman. Mulai dengan "Tentang Jawsel" atau "Cara Bermain".</p>}
          </div>
        </Card>
        {form && (
          <Card testid="info-editor">
            <SectionTitle title={sel ? `Sunting: ${sel.title}` : "Halaman Baru"} right={<IconBtn testid="info-editor-close" onClick={() => { setForm(null); setEdit(null); }}><X size={14} /></IconBtn>} />
            <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <Field label="Judul" grow><input data-testid="info-title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="mis. Cara Bermain" style={inputStyle} required /></Field>
                <Field label="Slug (opsional)"><input data-testid="info-slug" value={form.slug} onChange={e => setForm({ ...form, slug: e.target.value })} placeholder="otomatis dari judul" style={{ ...inputStyle, width: 200 }} /></Field>
              </div>
              <GambarInfo token={token} form={form} setForm={setForm} />
              <Field label="Isi"><textarea data-testid="info-content" value={form.content} onChange={e => setForm({ ...form, content: e.target.value })} rows={12} placeholder="Tulis isi halaman..." style={{ ...inputStyle, resize: "vertical", lineHeight: 1.55 }} required /></Field>
              <label style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13, color: D.ink, fontWeight: 600 }}>
                <Toggle on={!!form.published} testid="info-form-published" label="Tayang" onChange={v => setForm({ ...form, published: v })} /> Tayang di aplikasi
              </label>
              <div style={{ display: "flex", gap: 8 }}>
                <Btn type="submit" testid="info-submit"><Info size={15} /> {edit ? "Simpan" : "Buat Halaman"}</Btn>
                <Btn variant="ghost" testid="info-cancel" onClick={() => { setForm(null); setEdit(null); }}>Batal</Btn>
              </div>
            </form>
          </Card>
        )}
      </div>
    </div>
  );
}
